import type { AdminAsset, PricingPlanItem, VersionedPricing } from '@diana/contracts';
import { ArrowLeft, ImageIcon, Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useEditorCommandRegistration } from '../../app/EditorCommandContext.js';
import { ConflictDialog } from '../../components/ConflictDialog.js';
import { RichTextField, plainToRichText, richTextToPlain } from '../../components/RichTextField.js';
import { SortableList } from '../../components/SortableList.js';
import { readRevisionConflict } from '../../lib/use-conflict.js';
import { useAutosave } from '../../lib/use-autosave.js';
import { ContextualMediaField } from '../media/ContextualMediaField.js';
import { EditorHeading, SelectField, TextField } from '../settings/SettingsEditor.js';
import {
  Button,
  Checkbox,
  CollectionMeta,
  CollectionRow,
  CollectionSurface,
  CollectionThumbnail,
  IconButton,
} from '../../ui/index.js';
import { packageDurationMinutes } from '../crm/package-duration.js';
import './PricingEditor.css';

type Collection = 'plans' | 'extras' | 'steps' | 'faq';

type PricingEditorProps = {
  pricing: VersionedPricing;
  assets?: readonly AdminAsset[];
  planId?: string;
  extrasHref?: string;
  saveCollection(
    collection: Collection,
    expectedRevision: number,
    items: VersionedPricing[Collection],
  ): Promise<VersionedPricing>;
};

export function PricingEditor({
  pricing,
  assets = [],
  planId,
  extrasHref = '/pages',
  saveCollection,
}: PricingEditorProps) {
  const saved = useRef(pricing);
  const deletedPlan = useRef<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<PricingPlanItem | null>(null);
  const navigate = useNavigate();
  const autosave = useAutosave({
    initialValue: pricing,
    initialRevision: pricing.revision,
    save: async ({ value, expectedRevision }) => {
      let server = saved.current;
      let revision = expectedRevision;
      for (const collection of ['plans'] as const) {
        if (same(server[collection], value[collection])) continue;
        server = await saveCollection(collection, revision, value[collection]);
        revision = server.revision;
      }
      saved.current = server;
      if (deletedPlan.current && !server.plans.some((plan) => plan.id === deletedPlan.current)) {
        deletedPlan.current = null;
        navigate('/packages', { replace: true });
      }
      return { value: server, revision };
    },
    readConflict: (error) =>
      readRevisionConflict(error, (current) => (isPricing(current) ? current : null)),
  });
  const draft = autosave.snapshot.value;
  const selectedPlan = planId ? draft.plans.find((plan) => plan.id === planId) : undefined;
  useEditorCommandRegistration({
    status: autosave.snapshot.status,
    save: () => void autosave.flush(),
  });
  const update = <Key extends Collection>(collection: Key, items: VersionedPricing[Key]) =>
    autosave.edit({ ...draft, [collection]: items });

  return (
    <article className="editor pricing-editor">
      {planId ? (
        <>
          <Link className="dui-back-link pricing-editor__back" to="/packages">
            <ArrowLeft size={14} aria-hidden="true" /> Back to Packages
          </Link>
          {selectedPlan ? (
            <>
              <EditorHeading
                title={selectedPlan.payload.title}
                status={autosave.snapshot.status}
                onRetry={() => void autosave.retry()}
              />
              <PackageEditor
                plan={selectedPlan}
                assets={assets}
                onChange={(changed) => updatePlan(changed)}
                onRemove={() => {
                  deletedPlan.current = selectedPlan.id;
                  update(
                    'plans',
                    normalize(draft.plans.filter((item) => item.id !== selectedPlan.id)),
                  );
                  void autosave.flush();
                }}
              />
            </>
          ) : (
            <div className="pricing-package-missing">
              <EditorHeading
                title="Package not found"
                status={autosave.snapshot.status}
                onRetry={() => void autosave.retry()}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <Link className="dui-back-link pricing-editor__back" to="/site">
            <ArrowLeft size={18} aria-hidden="true" /> Back to Site
          </Link>
          <EditorHeading
            title="Packages"
            status={autosave.snapshot.status}
            onRetry={() => void autosave.retry()}
            action={
              <Button
                variant="primary"
                leadingIcon={Plus}
                onClick={() => update('plans', [...draft.plans, newPlan(draft.plans.length)])}
              >
                Add package
              </Button>
            }
          />

          <section className="pricing-package-list" aria-label="Pricing packages">
            <div className="pricing-package-list__heading">
              <h2>Package order</h2>
              <span>Drag or use arrows to reorder</span>
            </div>
            <CollectionSurface>
              <SortableList
                items={draft.plans}
                label={(plan) => plan.payload.title}
                onChange={(plans) => update('plans', plans)}
                render={(plan) => (
                  <PackageCard
                    plan={plan}
                    assets={assets}
                    onDelete={() => setDeleteCandidate(plan)}
                  />
                )}
              />
            </CollectionSurface>
          </section>
        </>
      )}

      {!planId ? (
        <section className="pricing-extras" aria-labelledby="pricing-extras-title">
          <h2 id="pricing-extras-title">Add-ons</h2>
          <p>Extras shown on the Pricing page are managed with that page’s content.</p>
          <Link to={extrasHref}>Manage extras on Pricing page</Link>
        </section>
      ) : null}

      {deleteCandidate ? (
        <PackageDeleteDialog
          plan={deleteCandidate}
          onCancel={() => setDeleteCandidate(null)}
          onConfirm={() => {
            const candidate = deleteCandidate;
            setDeleteCandidate(null);
            update('plans', normalize(draft.plans.filter((item) => item.id !== candidate.id)));
            void autosave.flush();
          }}
        />
      ) : null}

      <ConflictDialog
        open={autosave.snapshot.status === 'Conflict'}
        onAcceptServer={autosave.acceptServerVersion}
        onReapplyLocal={autosave.reapplyLocal}
        onCopyLocal={() =>
          void navigator.clipboard?.writeText(JSON.stringify(autosave.copyLocal(), null, 2))
        }
      />
    </article>
  );

  function updatePlan(changed: PricingPlanItem) {
    update(
      'plans',
      draft.plans.map((item) => (item.id === changed.id ? changed : item)),
    );
  }
}

function PackageCard({
  plan,
  assets,
  onDelete,
}: {
  plan: PricingPlanItem;
  assets: readonly AdminAsset[];
  onDelete(): void;
}) {
  const cover = assets.find((asset) => asset.id === plan.payload.cover?.assetId);
  const coverSrc = cover
    ? `/api/v1/admin/assets/${cover.id}/preview`
    : plan.payload.cover
      ? `/api/v1/admin/assets/${plan.payload.cover.assetId}/preview`
      : null;

  return (
    <CollectionRow className="pricing-package-row">
      <Link className="pricing-package-card" to={`/packages/${plan.id}`}>
        <CollectionThumbnail className="pricing-package-card__cover">
          {coverSrc ? (
            <img src={coverSrc} alt={`${plan.payload.title} package cover`} />
          ) : (
            <ImageIcon size={22} aria-hidden="true" />
          )}
        </CollectionThumbnail>
        <span className="pricing-package-card__copy">
          <strong>{plan.payload.title}</strong>
          <span className="pricing-package-card__description">
            {richTextToPlain(plan.payload.description)}
          </span>
          <span className="pricing-package-card__duration">
            {plan.payload.durationLabel || 'Duration not set'}
          </span>
          <span className="pricing-package-card__placements">
            {(plan.payload.placements?.pricing ?? true) ? <span>Pricing</span> : null}
            {(plan.payload.placements?.home ?? true) ? <span>Homepage</span> : null}
          </span>
        </span>
        <CollectionMeta className="pricing-package-card__price" label="Price">
          {plan.payload.priceLabel}
        </CollectionMeta>
      </Link>
      <IconButton
        className="pricing-package-row__delete"
        icon={Trash2}
        label={`Delete ${plan.payload.title} package`}
        variant="quiet"
        size="small"
        onClick={onDelete}
      />
    </CollectionRow>
  );
}

function PackageEditor({
  plan,
  assets,
  onChange,
  onRemove,
}: {
  plan: PricingPlanItem;
  assets: readonly AdminAsset[];
  onChange(plan: PricingPlanItem): void;
  onRemove(): void;
}) {
  const payload = plan.payload;
  const service = payload.service ?? inferService(plan);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const updatePayload = (patch: Partial<typeof payload>) =>
    onChange({ ...plan, payload: { ...payload, ...patch } });
  return (
    <div className="package-editor">
      <div className="package-editor__overview">
        <div className="package-editor__cover">
          <ContextualMediaField
            title="Package cover"
            profile="standard"
            cropProfile={service === 'Proposals' ? 'portrait-2-3' : 'landscape-4-3'}
            value={payload.cover}
            assets={assets}
            onChange={(cover) => updatePayload({ cover })}
          />
        </div>

        <section
          className="package-editor__section package-editor__section--overview"
          aria-labelledby="package-details-title"
        >
          <h2 id="package-details-title">Package details</h2>
          <div className="package-editor__fields">
            <div className="form-grid form-grid--two">
              <TextField
                label="Package title"
                value={payload.title}
                onChange={(title) => updatePayload({ title })}
              />
              <SelectField
                label="Service"
                value={service}
                options={['Personal', 'Family', 'Proposals', 'Weddings']}
                onChange={(service) =>
                  updatePayload({ service: service as NonNullable<typeof payload.service> })
                }
              />
            </div>
            <div className="form-grid form-grid--two">
              <TextField
                label="Price"
                value={payload.priceLabel}
                onChange={(priceLabel) => updatePayload({ priceLabel })}
              />
              <TextField
                label="Duration"
                value={payload.durationLabel ?? ''}
                optional
                onChange={(durationLabel) =>
                  updatePayload({ durationLabel: durationLabel || undefined })
                }
              />
            </div>
            <label className="field">
              <span>CRM duration (hours)</span>
              <input
                aria-label="CRM duration (hours)"
                type="number"
                min="0.016667"
                max="168"
                step="any"
                inputMode="decimal"
                value={packageDurationMinutes(payload) / 60}
                onChange={(event) => {
                  const value = Number(event.currentTarget.value);
                  if (Number.isFinite(value) && value > 0 && value <= 168)
                    updatePayload({ durationMinutes: Math.max(1, Math.round(value * 60)) });
                }}
              />
              <small>Default duration when scheduling this package.</small>
            </label>
            <RichTextField
              label="Description"
              value={payload.description}
              onChange={(description) => updatePayload({ description: description! })}
            />
            <div className="home-choice-list package-editor__placements">
              <div className="home-choice">
                <Checkbox
                  label="Show on Pricing"
                  checked={payload.placements?.pricing ?? true}
                  onChange={(event) =>
                    updatePayload({
                      placements: {
                        pricing: event.currentTarget.checked,
                        home: payload.placements?.home ?? true,
                      },
                    })
                  }
                />
              </div>
              <div className="home-choice">
                <Checkbox
                  label="Show on Homepage"
                  checked={payload.placements?.home ?? true}
                  onChange={(event) =>
                    updatePayload({
                      placements: {
                        pricing: payload.placements?.pricing ?? true,
                        home: event.currentTarget.checked,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="package-editor__section" aria-labelledby="package-includes-title">
        <h2 id="package-includes-title">What is included</h2>
        <div className="feature-list">
          <SortableList
            items={plan.features}
            label={(feature) => `Feature ${plan.features.indexOf(feature) + 1}`}
            onChange={(features) => onChange({ ...plan, features })}
            render={(feature, index) => (
              <div className="feature-row" key={feature.id}>
                <TextField
                  label={`Feature ${index + 1}`}
                  value={feature.payload}
                  onChange={(text) => {
                    const features = [...plan.features];
                    features[index] = { ...feature, payload: text };
                    onChange({ ...plan, features });
                  }}
                />
                <IconButton
                  className="feature-row__delete"
                  icon={Trash2}
                  label={`Delete feature ${index + 1}`}
                  variant="quiet"
                  size="small"
                  onClick={() =>
                    onChange({
                      ...plan,
                      features: normalize(plan.features.filter((item) => item.id !== feature.id)),
                    })
                  }
                />
              </div>
            )}
          />
          <Button
            variant="secondary"
            onClick={() =>
              onChange({
                ...plan,
                features: [
                  ...plan.features,
                  {
                    id: crypto.randomUUID(),
                    position: plan.features.length,
                    payload: 'New feature',
                  },
                ],
              })
            }
          >
            Add feature
          </Button>
        </div>
      </section>

      <section className="package-editor__section" aria-labelledby="package-action-title">
        <h2 id="package-action-title">Action</h2>
        <div className="form-grid form-grid--two">
          <TextField
            label="Button label"
            value={payload.action.label}
            onChange={(label) => updatePayload({ action: { ...payload.action, label } })}
          />
          <TextField
            label="Button destination"
            value={payload.action.href}
            onChange={(href) =>
              updatePayload({
                action: { ...payload.action, href: href as typeof payload.action.href },
              })
            }
          />
        </div>
      </section>

      <section className="package-editor__danger">
        <div>
          <strong>Delete package</strong>
          <p>Delete this package from every page that uses the shared Pricing catalog.</p>
        </div>
        <Button variant="destructive" leadingIcon={Trash2} onClick={() => setConfirmDelete(true)}>
          Delete package
        </Button>
      </section>

      {confirmDelete ? (
        <PackageDeleteDialog
          plan={plan}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={onRemove}
        />
      ) : null}
    </div>
  );
}

function PackageDeleteDialog({
  plan,
  onCancel,
  onConfirm,
}: {
  plan: PricingPlanItem;
  onCancel(): void;
  onConfirm(): void;
}) {
  const titleId = `delete-package-${plan.id}`;
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="dialog" role="alertdialog" aria-modal="true" aria-labelledby={titleId}>
        <p className="eyebrow">Permanent action</p>
        <h2 id={titleId}>Delete “{plan.payload.title}”?</h2>
        <p>
          This package will be removed from Pricing and every other page where it is currently
          shown. This cannot be undone after the next save.
        </p>
        <div className="dialog__actions">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" leadingIcon={Trash2} onClick={onConfirm}>
            Delete package
          </Button>
        </div>
      </section>
    </div>
  );
}

function newPlan(position: number): PricingPlanItem {
  return {
    id: crypto.randomUUID(),
    key: `package-${position + 1}`,
    position,
    visibility: 'Public',
    payload: {
      title: 'New package',
      description: plainToRichText('Describe the experience.'),
      priceLabel: '0 €',
      service: 'Personal',
      placements: { pricing: true, home: true },
      action: { label: 'Book now', href: '/pricing', style: 'Primary' },
    },
    features: [],
  };
}

function inferService(plan: PricingPlanItem): NonNullable<PricingPlanItem['payload']['service']> {
  if (plan.payload.action.href === '/weddings') return 'Weddings';
  if (plan.payload.action.href === '/proposals') return 'Proposals';
  return /family|couple/i.test(plan.payload.title) ? 'Family' : 'Personal';
}

function normalize<Item extends { position: number }>(items: Item[]) {
  return items.map((item, position) => ({ ...item, position }));
}

function same(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function isPricing(value: unknown): value is VersionedPricing {
  return (
    !!value &&
    typeof value === 'object' &&
    'catalogKey' in value &&
    'plans' in value &&
    'revision' in value
  );
}
